import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Zap, Star, ImageIcon, ShoppingBag, Loader2 } from "lucide-react";
import { formatINR } from "../data/products";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { buyNow } from "../lib/buyNow";
import { reviewCountFor } from "../lib/ratings";

const ProductCard = ({ product, index }) => {
  const { addItem, openCart, cartQty } = useCart();
  const [buying, setBuying] = useState(false);

  const inCart = cartQty ? cartQty(product.id) : 0;
  const soldOut = product.availableForSale === false || product.currentlyNotInStock === true || product.quantityAvailable === 0;
  const stockLeft = typeof product.quantityAvailable === "number" ? product.quantityAvailable : null;
  const atMax = stockLeft !== null && inCart >= stockLeft;
  const lowStock = stockLeft !== null && stockLeft > 0 && stockLeft <= 5;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (soldOut) return;
    if (atMax) {
      toast.error(`Only ${stockLeft} in stock — you already have ${inCart} in your box.`);
      return;
    }
    addItem(product);
    toast.success(`${product.name} added to your festive box`, {
      description: "Express 2-Day delivery applied.",
      action: { label: "View", onClick: openCart },
    });
  };

  const handleBuyNow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (soldOut) return;
    buyNow({
      variantId: product.variantId,
      id: product.id,
      name: product.name,
      price: product.price,
      onStart: () => setBuying(true),
      onEnd: () => setBuying(false),
    });
  };

  const discount = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : null;

  const to = `/product/${product.id}`;

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: (index % 8) * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-[#F3E5AB]/10 bg-[#111826] transition-all duration-500 hover:border-[#D4AF37]/50 hover:shadow-[0_0_40px_rgba(212,175,55,0.15)]"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={to} data-testid={`product-card-link-${product.id}`} className="flex flex-1 flex-col">
        {/* Image */}
        <div className="relative aspect-square w-full overflow-hidden">
          {product.image ? (
            <img src={product.image} alt={product.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          ) : (
            <div className="shimmer flex h-full w-full items-center justify-center transition-transform duration-700 group-hover:scale-105" data-testid={`product-image-placeholder-${product.id}`}>
              <div className="absolute left-1/2 top-1/2 h-2/3 w-2/3 -translate-x-1/2 -translate-y-1/2 bloom-gold opacity-50" />
              <div className="relative flex flex-col items-center gap-2 text-[#D4AF37]/40">
                <ImageIcon className="h-8 w-8" strokeWidth={1} />
                <span className="font-sans text-[10px] uppercase tracking-[0.25em]">Image coming soon</span>
              </div>
            </div>
          )}

          <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full border border-[#D4AF37]/50 bg-[#0B0F19]/70 px-2.5 py-1 backdrop-blur-md" data-testid={`express-tag-${product.id}`}>
            <Zap className="h-3 w-3 text-[#D4AF37]" fill="#D4AF37" strokeWidth={0} />
            <span className="font-sans text-[10px] font-medium uppercase tracking-[0.15em] text-[#F3E5AB]">Express 2-Day</span>
          </div>

          {product.badge && product.badge !== "Sold Out" && (
            <div className="absolute right-3 top-3 rounded-full bg-[#A6324F] px-2.5 py-1">
              <span className="font-sans text-[10px] font-medium uppercase tracking-[0.12em] text-white">{product.badge}</span>
            </div>
          )}
        </div>

        <div className="flex flex-1 flex-col p-5">
          <div className="mb-2 flex items-center gap-2">
            <Star className="h-3.5 w-3.5 text-[#D4AF37]" fill="#D4AF37" strokeWidth={0} />
            <span className="font-sans text-xs text-[#F3EEE3]">{product.rating.toFixed(1)}</span>
            <span className="font-sans text-xs text-[#8B93A7]">({reviewCountFor(product.id)})</span>
            {product.minOrder && (
              <span className="ml-auto rounded-full border border-[#F3E5AB]/12 px-2 py-0.5 font-sans text-[10px] uppercase tracking-wider text-[#8B93A7]">
                Min. {product.minOrder}
              </span>
            )}
          </div>

          <h3 className="font-serif text-xl leading-tight tracking-tight text-[#F3EEE3] transition-colors duration-300 group-hover:text-[#D4AF37]">
            {product.name}
          </h3>

          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-serif text-xl font-semibold text-[#D4AF37]">{formatINR(product.price)}</span>
            {product.originalPrice && (
              <span className="font-sans text-sm text-[#8B93A7] line-through">{formatINR(product.originalPrice)}</span>
            )}
            {discount && <span className="font-sans text-xs font-medium text-[#F3E5AB]">{discount}% off</span>}
          </div>
        </div>
      </Link>

      <div className="mx-5 mb-5 flex gap-2">
        <button
          onClick={handleAdd}
          data-testid={`add-to-cart-${product.id}`}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 py-3 font-sans text-xs font-medium tracking-wide text-[#F3E5AB] transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/15"
        >
          <ShoppingBag className="h-3.5 w-3.5" strokeWidth={1.8} />
          Add
        </button>
        <button
          onClick={handleBuyNow}
          disabled={buying}
          data-testid={`buy-now-${product.id}`}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-[#D4AF37] bg-[#D4AF37] py-3 font-sans text-xs font-semibold tracking-wide text-[#0B0F19] transition-all duration-300 hover:bg-[#F3E5AB] hover:shadow-[0_0_22px_rgba(212,175,55,0.45)] disabled:opacity-70"
        >
          {buying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" fill="#0B0F19" strokeWidth={0} />}
          Buy Now
        </button>
      </div>
    </motion.article>
  );
};

export default ProductCard;
